// PORTAL MODE — the cortex frontend doubles as the read-only portal shipped
// with every standalone deployment (Deployments feature). The portal nginx
// serves /portal-config.js defining window.__PORTAL__:
//   { portal: true, projectSlug, branch, flowUrl, views }
// In cortex itself the file is an empty 200 (nginx stub) and all of this is
// inert. Portal mode: LOGIN-GATED (the in-app portal-api authenticates
// against the deploy-time user snapshot — no baked credential), pin the
// project, trim the nav to Apps/Agents/Workflows/Data Governance/Pipeline
// (+ "Flow" linking to the exported endpoints dashboard), and hide
// build/request CTAs — a limited, read-only cortex.

export const portalConfig = () => (typeof window !== "undefined" && window.__PORTAL__) || null;

export const isPortal = () => !!portalConfig()?.portal;

// EMBED MODE — an app page (a deployed view domain, or the in-cortex Apps
// embed) iframes single SPA surfaces (e.g. Workflows) chromeless: ?embed=1
// hides the nav shell and the floating chat. Captured ONCE at SPA boot as
// MODULE STATE, deliberately not sessionStorage: on the platform the embed
// iframe is SAME-ORIGIN with the host SPA and would leak a tab-wide flag into
// it. Module state lives and dies with this browsing context's document, and
// in-SPA navigations (which drop the query param) keep it.
let EMBED_MODE = false;
let EMBED_HOST = "";
try {
  const q = new URLSearchParams(window.location.search);
  EMBED_MODE = q.get("embed") === "1";
  EMBED_HOST = q.get("host") || "";
} catch { /* no window */ }
export const isEmbedMode = () => EMBED_MODE;
// WHO frames this embed. `host=app-sdk` = an APP SERVICE's page through the
// cortex app SDK (shared/app-sdk): a guide launched in this frame must run on
// the SERVICE ITSELF, not inside this nested frame — Layout hands the tour up
// by postMessage instead of mounting the tutorial engine here (2026-09-08).
export const isAppSdkHost = () => EMBED_MODE && EMBED_HOST === "app-sdk";

// Called once before React renders (main.jsx): pin the URL to the project's
// Apps page. Auth is the normal login flow — AuthContext finds no (or an
// expired) token and routes to /login, which posts to the IN-APP portal-api.
// Session handoff IN the fragment: `#token=<bearer>` is stored and stripped
// before AuthContext reads localStorage, so the user isn't asked to sign in
// twice. Two callers hand a session over this way: a deployed view domain
// opening its portal (portal mode), and an APP SERVICE opening a companion
// page — Pipeline, Workflows, Guides… — chromeless (?embed=1) from ITS origin
// through the cortex app SDK (shared/app-sdk): the service is cross-origin, so
// cortex's own storage is a different bucket and the fragment is the only way
// the signed-in session reaches this frame (2026-09-08).
const consumeTokenHandoff = () => {
  try {
    const hm = (window.location.hash || "").match(/[#&]token=([^&]+)/);
    if (hm) {
      localStorage.setItem("access_token", decodeURIComponent(hm[1]));
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  } catch { /* storage unavailable — normal login flow takes over */ }
};

export const bootstrapPortal = () => {
  if (EMBED_MODE) consumeTokenHandoff();
  const cfg = portalConfig();
  if (!cfg?.portal) return;
  consumeTokenHandoff();
  const slug = cfg.projectSlug;
  const branch = cfg.branch || "main";
  if (slug && (window.location.pathname === "/" || window.location.pathname === "/login")) {
    window.history.replaceState(null, "", `/p/${slug}/b/${branch}/apps`);
  }
};
